import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";

import { appelerApi, corpsJson } from "../api/client";
import { utiliserSession } from "../auth/SessionContext";

interface ImportObjet {
  id: number;
  type_objet: string;
  identifiant_origine: string;
  donnees_brutes: Record<string, unknown>;
  statut: string;
  score_meilleure_suggestion: string | null;
  suggestions: Array<{
    production_id: number;
    titre: string;
    compagnie?: string;
    score: number;
  }>;
}

interface ListeImports {
  elements: ImportObjet[];
  pagination: { total: number };
}

export function ImportsPage() {
  const { session } = utiliserSession();
  const [liste, definirListe] = useState<ListeImports | null>(null);
  const [cibles, definirCibles] = useState<Record<number, string>>({});
  const [message, definirMessage] = useState("");
  const [actualisation, definirActualisation] = useState(0);

  useEffect(() => {
    if (session?.utilisateur.role_global !== "ADMIN_TICKBOSS") return;
    appelerApi<ListeImports>("/imports/a-raccorder", {}, true)
      .then(definirListe)
      .catch((cause: Error) => definirMessage(cause.message));
  }, [session, actualisation]);

  if (!session) return <Navigate to="/connexion" replace />;
  if (session.utilisateur.role_global !== "ADMIN_TICKBOSS") {
    return <Navigate to="/espace" replace />;
  }

  async function raccorder(importObjet: ImportObjet) {
    const cible =
      cibles[importObjet.id] ||
      String(importObjet.suggestions[0]?.production_id ?? "");
    if (!cible) {
      definirMessage("Indiquez une production cible.");
      return;
    }
    try {
      await appelerApi(
        `/imports/${importObjet.id}/raccorder`,
        {
          method: "POST",
          body: corpsJson({
            production_id: Number(cible),
            motif: "Correspondance validée depuis l’espace de raccordement"
          })
        },
        true
      );
      definirMessage("Donnée raccordée et opération journalisée.");
      definirActualisation((valeur) => valeur + 1);
    } catch (cause) {
      definirMessage(cause instanceof Error ? cause.message : "Raccordement impossible");
    }
  }

  async function ignorer(importObjet: ImportObjet) {
    try {
      await appelerApi(
        `/imports/${importObjet.id}/ignorer`,
        {
          method: "POST",
          body: corpsJson({ motif: "Donnée hors périmètre validée par l’administration" })
        },
        true
      );
      definirMessage("Donnée ignorée et décision journalisée.");
      definirActualisation((valeur) => valeur + 1);
    } catch (cause) {
      definirMessage(cause instanceof Error ? cause.message : "Action impossible");
    }
  }

  return (
    <section className="page imports-page">
      <header className="page__entete">
        <span className="sur-titre sur-titre--sombre">Administration Tickboss</span>
        <h1>Données Live à raccorder</h1>
        <p>
          Les données restent en zone tampon jusqu’à une décision explicite. Chaque
          suggestion expose son score et chaque action est historisée.
        </p>
      </header>
      <div className="barre-imports">
        <strong>{liste?.pagination.total ?? 0} donnée(s) en attente</strong>
        <span>Source · objet · identifiant d’origine</span>
      </div>
      {message && <p className="alerte alerte--succes">{message}</p>}
      <div className="liste-imports">
        {liste?.elements.map((objet) => {
          const titre = String(
            objet.donnees_brutes.titre ||
              objet.donnees_brutes.nom ||
              objet.identifiant_origine
          );
          return (
            <article key={objet.id}>
              <header>
                <span>{objet.type_objet}</span>
                <small>{objet.identifiant_origine}</small>
                <strong>{titre}</strong>
              </header>
              <div className="suggestions">
                {objet.suggestions.length ? (
                  objet.suggestions.map((suggestion) => (
                    <label key={suggestion.production_id}>
                      <input
                        type="radio"
                        name={`cible-${objet.id}`}
                        value={suggestion.production_id}
                        checked={
                          (cibles[objet.id] || String(objet.suggestions[0]?.production_id)) ===
                          String(suggestion.production_id)
                        }
                        onChange={(e) =>
                          definirCibles((valeurs) => ({
                            ...valeurs,
                            [objet.id]: e.target.value
                          }))
                        }
                      />
                      <span>
                        <strong>{suggestion.titre}</strong>
                        <small>{suggestion.compagnie || "Compagnie non renseignée"}</small>
                      </span>
                      <b>{suggestion.score.toFixed(0)} %</b>
                    </label>
                  ))
                ) : (
                  <label className="champ">
                    <span>Identifiant de la production cible</span>
                    <input
                      type="number"
                      min="1"
                      value={cibles[objet.id] || ""}
                      onChange={(e) =>
                        definirCibles((valeurs) => ({
                          ...valeurs,
                          [objet.id]: e.target.value
                        }))
                      }
                    />
                  </label>
                )}
              </div>
              <footer>
                <button className="bouton bouton--primaire" type="button" onClick={() => raccorder(objet)}>
                  Raccorder
                </button>
                <button className="bouton bouton--discret" type="button" onClick={() => ignorer(objet)}>
                  Ignorer
                </button>
              </footer>
            </article>
          );
        })}
      </div>
      {liste && !liste.elements.length && (
        <div className="etat-vide">
          <strong>Tout est raccordé.</strong>
          <p>Aucune donnée Live n’attend de décision.</p>
        </div>
      )}
    </section>
  );
}
